import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { trackAdsConversion } from "../lib/gtagConversions";
import { track } from "../lib/analytics";
import { ArrowRight } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import LanguageSwitcher from "../components/app/LanguageSwitcher";

export default function RegisterPage() {
  // Non è una landing SEO: noindex (esclusa anche dalla sitemap).
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    m.setAttribute("content", "noindex, follow");
    return () => { m.remove(); };
  }, []);
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  // Only what is strictly needed to open the account. VAT, address, country
  // and operator name are collected later (Impostazioni → profilo officina).
  const [form, setForm] = useState({ email: "", password: "", company_name: "" });
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setFieldErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  // Backend problems are always translated here: the user never sees a raw
  // Pydantic / HTTP message.
  const applyError = (res) => {
    const report = (field, key) => {
      if (field) setFieldErrors({ [field]: { key } });
      const message = t(`auth.fieldErrors.${key}`, t("auth.registerErrors.generic"));
      setFormError(field ? message : t(`auth.registerErrors.${key}`, message));
      track("register_error", { field: field || null, message, status: res.status || null });
    };
    if (res.network) return report(null, "network");
    if (res.code === "EMAIL_ALREADY_REGISTERED") return report("email", "emailTaken");
    if (res.code === "INVALID_VAT") return report("vat_number", "invalidVat");
    if (res.code === "NAME_REQUIRED") return report("company_name", "required");
    const fields = Object.keys(res.validation || {});
    if (fields.length) {
      setFieldErrors(res.validation);
      const first = res.validation[fields[0]];
      const message = t(`auth.fieldErrors.${first.key}`, first.params || {});
      setFormError(t("auth.registerErrors.checkFields"));
      return track("register_error", { field: fields[0], message, status: res.status || null });
    }
    report(null, "generic");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError("");
    setFieldErrors({});
    const payload = {
      email: form.email.trim(),
      password: form.password,
      company_name: form.company_name.trim(),
    };
    const res = await register(payload);
    setBusy(false);
    if (!res.ok) return applyError(res);
    track("register_completed", { method: "email" });
    trackAdsConversion("sign_up", { method: "email" });
    if (res.data?.dev_verification_url) {
      try { sessionStorage.setItem("mp_dev_verify_url", res.data.dev_verification_url); } catch { /* storage off */ }
    }
    navigate("/dashboard", { replace: true });
  };

  const FieldError = ({ name }) => {
    const err = fieldErrors[name];
    if (!err) return null;
    return (
      <p className="mt-1.5 text-xs text-red-400" data-testid={`register-error-${name}`}>
        {t(`auth.fieldErrors.${err.key}`, err.params || {})}
      </p>
    );
  };

  const inputCls = (name) =>
    `w-full px-4 py-2.5 rounded-md text-sm ${fieldErrors[name] ? "!border-red-500/70" : ""}`;

  return (
    <div className="theme-swiss min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <form onSubmit={submit} className="w-full max-w-md fade-in-up" data-testid="register-form" noValidate>
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-home">
            <BrandIcon size={40} />
            <div className="font-display font-bold text-xl text-slate-100 group-hover:text-orange-300 transition-colors">MachinePilot</div>
          </Link>
          <LanguageSwitcher compact />
        </div>
        <div className="label-tech mb-2">{t("auth.registration")}</div>
        <h2 className="font-display font-bold text-3xl text-slate-100">{t("auth.registerTitle")}</h2>
        <p className="text-slate-400 mt-2 text-sm">{t("auth.registerSubtitleShort")}</p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="label-tech block mb-2">{t("auth.workshopName")} <span className="text-red-400">*</span></label>
            <input value={form.company_name} onChange={update("company_name")} required maxLength={150}
              placeholder={t("auth.companyPlaceholder")} className={inputCls("company_name")} data-testid="register-company"/>
            <FieldError name="company_name"/>
          </div>
          <div>
            <label className="label-tech block mb-2">{t("auth.businessEmail")} <span className="text-red-400">*</span></label>
            <input type="email" value={form.email} onChange={update("email")} required autoComplete="email"
              className={inputCls("email")} data-testid="register-email"/>
            <FieldError name="email"/>
          </div>
          <div>
            <label className="label-tech block mb-2">{t("auth.password")} <span className="text-red-400">*</span> <span className="text-slate-500 text-[10px]">{t("auth.passwordHint")}</span></label>
            <input type="password" value={form.password} onChange={update("password")} required minLength={6}
              autoComplete="new-password" className={inputCls("password")} data-testid="register-password"/>
            <FieldError name="password"/>
          </div>
        </div>

        {formError && (
          <div className="mt-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="register-error">{formError}</div>
        )}

        <button type="submit" disabled={busy} className="btn-primary w-full mt-6 px-5 py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-50" data-testid="register-submit">
          {busy ? t("auth.creating") : (<>{t("auth.createAccount")} <ArrowRight size={16}/></>)}
        </button>

        <p className="text-xs text-slate-500 mt-3 text-center">
          {t("auth.afterRegisterShort")}
        </p>

        <div className="mt-6 text-sm text-slate-400 text-center">
          {t("auth.hasAccount")}{" "}
          <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="login-link">{t("auth.login")}</Link>
        </div>
      </form>
    </div>
  );
}
